import { isActivityType, type ActivityType } from './types'

export interface ActivityRow {
  id: string
  prospect_id: string
  user_id: string
  activity_type: ActivityType
  channel: string | null
  occurred_at: string
  description: string | null
  result: string | null
  received_response: boolean
  next_follow_up_at: string | null
  objection_id: string | null
  old_status: string | null
  new_status: string | null
  attachment_url: string | null
  created_at: string
}

export interface ActivityInsert {
  prospect_id: string
  user_id: string
  activity_type: ActivityType
  occurred_at: string
  channel?: string | null
  description?: string | null
  result?: string | null
  received_response?: boolean
  next_follow_up_at?: string | null
  objection_id?: string | null
  old_status?: string | null
  new_status?: string | null
  attachment_url?: string | null
}

export interface ActivityUpdate {
  prospect_id?: string | null
  user_id?: string | null
  activity_type?: ActivityType | null
  occurred_at?: string | null
  channel?: string | null
  description?: string | null
  result?: string | null
  received_response?: boolean
  next_follow_up_at?: string | null
  objection_id?: string | null
  old_status?: string | null
  new_status?: string | null
  attachment_url?: string | null
}

export const ALLOWED_ACTIVITY_COLUMNS = [
  'prospect_id',
  'user_id',
  'activity_type',
  'channel',
  'occurred_at',
  'description',
  'result',
  'received_response',
  'next_follow_up_at',
  'objection_id',
  'old_status',
  'new_status',
  'attachment_url',
] as const

type ActivityColumn = (typeof ALLOWED_ACTIVITY_COLUMNS)[number]
type ActivityPayloadSource = Partial<Record<ActivityColumn, unknown>>

const ALLOWED_ACTIVITY_COLUMN_SET = new Set<string>(ALLOWED_ACTIVITY_COLUMNS)
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export class UnknownActivityColumnsError extends Error {
  constructor(public readonly unknownKeys: string[]) {
    super(`Unknown Activity Columns: ${unknownKeys.join(', ')}`)
    this.name = 'UnknownActivityColumnsError'
  }
}

export function assertValidActivityPayload(payload: object): void {
  const unknownKeys = Object.keys(payload).filter(
    (key) => !ALLOWED_ACTIVITY_COLUMN_SET.has(key),
  )

  if (unknownKeys.length > 0) {
    console.error('Unknown Activity Columns', unknownKeys)
    throw new UnknownActivityColumnsError(unknownKeys)
  }
}

export function buildActivityPayload(
  source: ActivityPayloadSource,
  mode: 'insert',
): ActivityInsert
export function buildActivityPayload(
  source: ActivityPayloadSource,
  mode: 'update',
): ActivityUpdate
export function buildActivityPayload(
  source: ActivityPayloadSource,
  mode: 'insert' | 'update' = 'insert',
): ActivityInsert | ActivityUpdate {
  assertValidActivityPayload(source)

  if (mode === 'insert') {
    const payload: ActivityInsert = {
      prospect_id: requireUuid(source.prospect_id, 'prospect_id'),
      user_id: requireUuid(source.user_id, 'user_id'),
      activity_type: requireActivityType(source.activity_type),
      occurred_at: requireDate(source.occurred_at, 'occurred_at'),
    }

    appendOptionalFields(payload, source)
    assertValidActivityPayload(payload)
    return payload
  }

  const payload: ActivityUpdate = {}
  appendOptionalFields(payload, source)

  if (hasValue(source, 'prospect_id')) {
    payload.prospect_id = normalizeUuid(source.prospect_id)
  }
  if (hasValue(source, 'user_id')) {
    payload.user_id = normalizeUuid(source.user_id)
  }
  if (hasValue(source, 'activity_type')) {
    payload.activity_type = normalizeActivityType(source.activity_type)
  }
  if (hasValue(source, 'occurred_at')) {
    payload.occurred_at = normalizeDate(source.occurred_at)
  }

  assertValidActivityPayload(payload)
  return payload
}

function appendOptionalFields(
  payload: ActivityInsert | ActivityUpdate,
  source: ActivityPayloadSource,
) {
  if (hasValue(source, 'channel')) {
    payload.channel = normalizeText(source.channel)
  }
  if (hasValue(source, 'description')) {
    payload.description = normalizeText(source.description)
  }
  if (hasValue(source, 'result')) {
    payload.result = normalizeText(source.result)
  }
  if (hasValue(source, 'received_response')) {
    if (typeof source.received_response !== 'boolean') {
      throw new Error('received_response debe ser un booleano.')
    }
    payload.received_response = source.received_response
  }
  if (hasValue(source, 'next_follow_up_at')) {
    payload.next_follow_up_at = normalizeDate(source.next_follow_up_at)
  }
  if (hasValue(source, 'objection_id')) {
    payload.objection_id = normalizeUuid(source.objection_id)
  }
  if (hasValue(source, 'old_status')) {
    payload.old_status = normalizeText(source.old_status)
  }
  if (hasValue(source, 'new_status')) {
    payload.new_status = normalizeText(source.new_status)
  }
  if (hasValue(source, 'attachment_url')) {
    payload.attachment_url = normalizeText(source.attachment_url)
  }
}

function hasValue(source: ActivityPayloadSource, key: ActivityColumn): boolean {
  return Object.prototype.hasOwnProperty.call(source, key)
}

function requireText(value: unknown, field: string): string {
  const normalized = normalizeText(value)
  if (!normalized) {
    throw new Error(`Ingresá un valor válido para ${field}.`)
  }
  return normalized
}

function requireActivityType(value: unknown): ActivityType {
  const normalized = normalizeActivityType(value)
  if (!normalized) {
    throw new Error('activity_type debe usar un valor permitido.')
  }
  return normalized
}

function normalizeActivityType(value: unknown): ActivityType | null {
  const normalized = normalizeText(value)
  return isActivityType(normalized) ? normalized : null
}

function requireUuid(value: unknown, field: string): string {
  const normalized = normalizeUuid(value)
  if (!normalized) {
    throw new Error(`El UUID para ${field} falta o no es válido.`)
  }
  return normalized
}

function requireDate(value: unknown, field: string): string {
  const normalized = normalizeDate(value)
  if (!normalized) {
    throw new Error(`Ingresá una fecha válida para ${field}.`)
  }
  return normalized
}

function normalizeText(value: unknown): string | null {
  if (typeof value !== 'string') return null
  return value.trim() || null
}

function normalizeUuid(value: unknown): string | null {
  const normalized = normalizeText(value)
  return normalized && UUID_PATTERN.test(normalized) ? normalized : null
}

function normalizeDate(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}
